import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

declare const process: { env: Record<string, string | undefined> };
const API = `${process.env.REACT_APP_BACKEND_URL}/api/pureh`;

type Row = {
  kind: "product" | "blend";
  id: string; name: string; code?: string;
  blendId?: string | null;
  cost: number | null; retail: number | null; currency: string | null;
  missing?: string[];
  retailByMarket?: Record<string, number | null>;
};

function RetailCell({ row, market, currency, onSaved }: {
  row: Row; market: string; currency: string; onSaved: (v: number | null) => void;
}) {
  const [val, setVal] = useState(row.retail != null ? String(row.retail) : "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const num = val.trim() === "" ? null : Number(val);
    if (num != null && (Number.isNaN(num) || num < 0)) return;
    if (num === row.retail) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/blend/blends/${row.blendId}`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ retail_by_market: { ...(row.retailByMarket || {}), [market]: num } }),
      });
      if (!res.ok) throw new Error(String(res.status));
      onSaved(num);
      toast.success("اتسجّل سعر البيع ✓");
    } catch (e: any) { toast.error(`فشل حفظ السعر: ${e.message}`); } finally { setSaving(false); }
  };
  return (
    <span className="flex items-center gap-1">
      <input type="number" className="bl-input" style={{ width: 90, opacity: saving ? 0.5 : 1 }} value={val}
        placeholder="—" onChange={(e) => setVal(e.target.value)} onBlur={save}
        onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
        data-testid={`margin-retail-input-${row.id}`} />
      <span className="bl-sub">{currency}</span>
    </span>
  );
}

const r2 = (n: number) => Math.round(n * 100) / 100;

function marginColor(pct: number | null, threshold: number): string {
  if (pct == null) return "var(--bl-ink-muted)";
  if (pct >= threshold) return "var(--bl-ok)";
  if (pct >= threshold - 15) return "#e8c56a";
  return "var(--bl-danger)";
}

async function get(path: string) {
  const res = await fetch(`${API}${path}`, { credentials: "include" });
  if (!res.ok) throw new Error(`${path}: ${res.status}`);
  return res.json();
}

export default function MarginBoardPage() {
  const [market, setMarket] = useState<"eg" | "sa">("eg");
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [threshold, setThreshold] = useState(50);
  const [revCost, setRevCost] = useState(0);
  const [revMargin, setRevMargin] = useState(60);

  useEffect(() => {
    setLoading(true);
    setErr(null);
    (async () => {
      // Single source: Product Master via the unified catalog view (46 products)
      const cat = await get(`/blend/catalog?includeArchived=1`);
      const cur = market === "eg" ? "EGP" : "SAR";
      const rows2: Row[] = await Promise.all(cat.map(async (p: any) => {
        if (p.kind === "original") {
          const cogs = await get(`/product-master/ventures/orvan/products/${p.id}/cogs?marketId=${market}`).catch(() => null);
          return {
            kind: "product" as const, id: p.id, name: p.name, code: p.productNumber,
            blendId: null,
            cost: cogs?.cogsPerBottle ?? null,
            retail: cogs?.retailPrice ?? p.retail_by_market?.[market] ?? null,
            currency: cogs?.currency ?? cur, missing: cogs?.missing || [],
          };
        }
        const c = await get(`/blend/catalog/${p.id}/cost?marketId=${market}&sizeMl=50`).catch(() => null);
        const miss = [...(c?.unmapped || []), ...((c?.needsPrice || []).length ? [`أسعار ناقصة: ${c.needsPrice.length} خامة`] : [])];
        return {
          kind: "blend" as const, id: p.id, name: p.name, code: p.code,
          blendId: p.blendId,
          cost: c?.costPerBottle ?? null,
          retail: p.retail_by_market?.[market] ?? null,
          currency: cur,
          retailByMarket: p.retail_by_market || {},
          missing: miss,
        };
      }));
      setRows(rows2);
    })().catch((e) => setErr(String(e.message || e))).finally(() => setLoading(false));
  }, [market]);

  const VAT: Record<"eg" | "sa", number> = { eg: 0.14, sa: 0.15 };
  const computed = useMemo(() => rows.map((r) => {
    // سعر التجزئة شامل الضريبة — الهامش يُحسب على صافي الإيراد (retail ÷ (1+VAT)) − التكلفة الصافية
    const vat = VAT[market];
    const net = r.retail != null ? r2(r.retail / (1 + vat)) : null;
    const vatAmt = r.retail != null && net != null ? r2(r.retail - net) : null;
    const profit = r.cost != null && net != null ? r2(net - r.cost) : null;
    const pct = profit != null && net ? r2((profit / net) * 100) : null;
    return { ...r, net, vatAmt, profit, pct };
  }), [rows, market]);

  if (loading) return <div className="bl-sub py-8 text-center" data-testid="margin-loading">جارٍ حساب الهامش…</div>;
  if (err) return <div className="bl-sub py-8 text-center" style={{ color: "var(--bl-danger)" }} data-testid="margin-error">{err}</div>;

  return (
    <div className="space-y-4" data-testid="margin-board-page">
      <div className="flex gap-2 items-center flex-wrap">
        <button className={`bl-tab ${market === "eg" ? "active" : ""}`} onClick={() => setMarket("eg")} data-testid="margin-market-eg">مصر · EGP</button>
        <button className={`bl-tab ${market === "sa" ? "active" : ""}`} onClick={() => setMarket("sa")} data-testid="margin-market-sa">السعودية · SAR</button>
        <div className="flex items-center gap-1 mr-auto">
          <span className="bl-sub">عتبة الهامش %</span>
          <input type="number" className="bl-input" style={{ width: 70 }} value={threshold}
            onChange={(e) => setThreshold(Number(e.target.value) || 0)} data-testid="margin-threshold-input" />
        </div>
      </div>

      <div className="bl-card">
        <div className="bl-eyebrow mb-2">لوحة الهامش — المنتجات الحقيقية + التركيبات</div>
        <table className="bl-table" data-testid="margin-table">
          <thead>
            <tr><th>المنتج/التركيبة</th><th>التكلفة/زجاجة (صافي)</th><th>سعر الرفّ (شامل)</th><th>الضريبة ({market === "eg" ? "14%" : "15%"})</th><th>صافي الإيراد</th><th>الربح</th><th>الهامش %</th></tr>
          </thead>
          <tbody>
            {computed.map((r) => (
              <tr key={`${r.kind}-${r.id}`} data-testid={`margin-row-${r.kind}-${r.id}`}>
                <td>
                  <span className="bl-en">{r.name}</span>
                  {r.kind === "blend" && <span className="bl-pill mr-1">مُرقّى من التركيبات</span>}
                  {r.missing && r.missing.length > 0 && (
                    <div className="bl-sub" style={{ fontSize: 10 }}>ناقص: {r.missing.join("، ")}</div>
                  )}
                </td>
                <td>{r.cost != null ? `${r.cost} ${r.currency}` : "—"}</td>
                <td>
                  {r.kind === "blend" ? (
                    <RetailCell row={r} market={market} currency={r.currency || ""} onSaved={(v) =>
                      setRows((prev) => prev.map((x) => x.kind === "blend" && x.id === r.id
                        ? { ...x, retail: v, retailByMarket: { ...(x.retailByMarket || {}), [market]: v } } : x))} />
                  ) : (r.retail != null ? `${r.retail} ${r.currency}` : "—")}
                </td>
                <td data-testid={`margin-vat-${r.kind}-${r.id}`}>{(r as any).vatAmt != null ? `${(r as any).vatAmt}` : "—"}</td>
                <td data-testid={`margin-net-${r.kind}-${r.id}`}>{(r as any).net != null ? `${(r as any).net}` : "—"}</td>
                <td style={{ color: r.profit != null && r.profit < 0 ? "var(--bl-danger)" : undefined }}>
                  {r.profit != null ? `${r.profit} ${r.currency}` : "—"}
                </td>
                <td style={{ color: marginColor(r.pct, threshold), fontWeight: 800 }}>
                  {r.pct != null ? `${r.pct}%` : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="bl-sub mt-2" style={{ fontSize: 10 }}>
          سعر الرفّ شامل الضريبة (مصر 14% · السعودية 15%) · الهامش = صافي الإيراد (بعد خصم الضريبة) − التكلفة الصافية · التكلفة من COGS الحقيقي.
        </p>
      </div>

      <div className="bl-card" data-testid="reverse-margin-calc">
        <div className="bl-eyebrow mb-2">حاسبة عكسية — عايز هامش X% تبيع بكام؟</div>
        <div className="flex gap-2 items-end flex-wrap">
          <div><label className="bl-label">التكلفة/زجاجة</label>
            <input type="number" className="bl-input" style={{ width: 130 }} value={revCost}
              onChange={(e) => setRevCost(Number(e.target.value) || 0)} data-testid="reverse-cost-input" /></div>
          <div><label className="bl-label">الهامش المستهدف %</label>
            <input type="number" className="bl-input" style={{ width: 110 }} value={revMargin}
              onChange={(e) => setRevMargin(Number(e.target.value) || 0)} data-testid="reverse-margin-input" /></div>
          <div className="bl-pill" style={{ fontSize: 14 }} data-testid="reverse-result">
            سعر البيع المطلوب: {revMargin < 100 && revCost > 0 ? r2(revCost / (1 - revMargin / 100)) : "—"}
          </div>
        </div>
      </div>
    </div>
  );
}
